import React, { useState } from 'react';
import { X, Calendar, Users, BedDouble, MessageSquare, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { supabase } from '../lib/supabase';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDates?: {checkIn: string, checkOut: string} | null;
}

export default function BookingModal({ isOpen, onClose, selectedDates }: BookingModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const [isCustomCode, setIsCustomCode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+39',
    customCountryCode: '',
    checkIn: '',
    checkOut: '',
    guests: '1',
    roomType: 'double',
    specialRequests: ''
  });

  // Update form data when selectedDates prop changes
  React.useEffect(() => {
    if (selectedDates) {
      setFormData(prev => ({
        ...prev,
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut
      }));
    }
  }, [selectedDates]);

  const countryCodes = [
    { code: '+39', country: 'Italy (+39) 🇮🇹' },
    { code: '+33', country: 'France (+33) 🇫🇷' },
    { code: '+44', country: 'UK (+44) 🇬🇧' },
    { code: '+49', country: 'Germany (+49) 🇩🇪' },
    { code: '+34', country: 'Spain (+34) 🇪🇸' },
    { code: '+1', country: 'USA (+1) 🇺🇸' },
    { code: '+1', country: 'Canada (+1) 🇨🇦' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const fullPhone = `${isCustomCode ? formData.customCountryCode : formData.countryCode} ${formData.phone}`;

      const { data: rooms, error: roomError } = await supabase
        .from('rooms')
        .select('id, name')
        .ilike('name', `%${formData.roomType}%`)
        .limit(1)
        .maybeSingle();

      if (roomError) {
        console.error('Error fetching room:', roomError);
        throw new Error('Unable to find room. Please try again.');
      }

      if (!rooms) {
        throw new Error('Room type not available. Please contact us directly.');
      }

      const { error: dbError } = await supabase
        .from('bookings')
        .insert({
          room_id: rooms.id,
          room_type: formData.roomType,
          guest_first_name: formData.firstName,
          guest_last_name: formData.lastName,
          guest_name: `${formData.firstName} ${formData.lastName}`,
          guest_email: formData.email,
          guest_phone: fullPhone,
          check_in: formData.checkIn,
          check_out: formData.checkOut,
          num_guests: parseInt(formData.guests),
          special_requests: formData.specialRequests || null,
          status: 'pending'
        });

      if (dbError) {
        console.error('Database error:', dbError);
        throw dbError;
      }

      const templateParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: fullPhone,
        checkInDate: formData.checkIn,
        checkOutDate: formData.checkOut,
        guests: formData.guests,
        roomType: formData.roomType,
        description: formData.specialRequests
      };

      try {
        await emailjs.send(
          'service_34bo5vc',
          'template_kalo352',
          templateParams,
          'HgoQxUdDOIjmpUxjz'
        );
      } catch (emailError) {
        console.error('Email notification failed, but booking was saved:', emailError);
      }

      setIsSuccess(true);
      setCountdown(15);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            setIsSuccess(false);
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              countryCode: '+39',
              customCountryCode: '',
              checkIn: '',
              checkOut: '',
              guests: '1',
              roomType: 'double',
              specialRequests: ''
            });
            setIsCustomCode(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert(t('booking.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleCustomCode = () => {
    setIsCustomCode(!isCustomCode);
    if (!isCustomCode) {
      setFormData(prev => ({ ...prev, customCountryCode: '+' }));
    }
  };

  if (!isOpen) return null;

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 md:p-10 max-w-md w-full text-center shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-serif text-stone-900 mb-4">{t('booking.submitSuccess')}</h2>
          <div className="bg-stone-50 rounded-lg p-6 mb-6 text-left space-y-2">
            <p className="text-stone-700"><span className="font-medium">{t('booking.firstName')}:</span> {formData.firstName}</p>
            <p className="text-stone-700"><span className="font-medium">{t('booking.email')}:</span> {formData.email}</p>
            <p className="text-stone-700"><span className="font-medium">{t('booking.checkIn')}:</span> {formData.checkIn}</p>
            <p className="text-stone-700"><span className="font-medium">{t('booking.roomType')}:</span> {formData.roomType}</p>
          </div>
          <div className="text-sm text-stone-500">
            {t('booking.autoClose')} ({countdown}s)
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl md:text-3xl font-serif text-stone-900">{t('booking.title')}</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors">
              <X className="w-5 h-5 text-stone-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('booking.firstName')} *
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('booking.lastName')} *
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('booking.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('booking.phone')} *
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="customCode"
                      checked={isCustomCode}
                      onChange={toggleCustomCode}
                      className="rounded border-gray-300 text-amber-700 focus:ring-yellow-500"
                    />
                    <label htmlFor="customCode" className="text-sm text-gray-600">
                      {t('booking.customCode')}
                    </label>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] md:grid-cols-[140px_1fr] gap-2">
                    {isCustomCode ? (
                      <input
                        type="text"
                        name="customCountryCode"
                        value={formData.customCountryCode}
                        onChange={handleChange}
                        placeholder="+XXX"
                        pattern="^\+[0-9]{1,4}$"
                        required
                        className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                      />
                    ) : (
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="w-full px-2 py-2 text-sm md:text-base border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500 bg-white appearance-none"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code + country.country} value={country.code}>
                            {country.country}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="123456789"
                      className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('booking.checkIn')} *
                </label>
                <input
                  type="date"
                  name="checkIn"
                  required
                  value={formData.checkIn}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('booking.checkOut')} *
                </label>
                <input
                  type="date"
                  name="checkOut"
                  required
                  value={formData.checkOut}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t('booking.guests')} *
                </label>
                <select
                  name="guests"
                  required
                  value={formData.guests}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                >
                  {[...Array(10)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {t(i === 0 ? 'booking.guest' : 'booking.guests')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <BedDouble className="w-4 h-4" />
                  {t('booking.roomType')} *
                </label>
                <select
                  name="roomType"
                  required
                  value={formData.roomType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                >
                  <option value="double">{t('rooms.types.double')}</option>
                  <option value="triple">{t('rooms.types.triple')}</option>
                  <option value="quadruple">{t('rooms.types.quadruple')}</option>
                  <option value="family">{t('rooms.types.family')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                {t('booking.specialRequests')}
              </label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 text-sm md:text-base border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                placeholder={t('booking.specialRequestsPlaceholder')}
              ></textarea>
            </div>

            <div className="flex flex-col md:flex-row justify-end gap-3 md:gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-stone-300 rounded-lg hover:bg-stone-50 text-sm md:text-base font-medium text-stone-700 transition-all"
                disabled={isSubmitting}
              >
                {t('booking.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base font-medium transition-all shadow-md hover:shadow-lg"
              >
                {isSubmitting ? t('booking.submitting') : t('booking.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}